import { notify } from 'src/clients/ApiService';
import { AppAPIName, BaseEntity, CONTRACTS_PAGE } from 'src/constants';
import {
  Contract,
  ContractField,
  ListContractResponse,
} from 'src/entities/Contract';
import history from 'src/history';
import { API } from 'src/utils/AmplifyApiUtils';
import { UserProfile } from 'src/utils/UserUtils';

import { ensureApiError } from '../../utils/Errors';

import { ApiMethods, ApiTags, appAPI } from '.';

export enum ContractFileProcessingStatus {
  Processing = 'processing',
  Ready = 'ready',
  Failed = 'failed',
}

export interface ContractFileResponse {
  fileKey: string;
  // Empty until the async page-to-image conversion completes.
  pageKeys: string[];
  identityId: string;
  warnings?: string[];
  status?: ContractFileProcessingStatus;
  processingStartedAt?: string;
  failureReason?: string;
}

// Kept as an alias for backwards-compat with callers that imported the old name.
type CreateContractFilesResponse = ContractFileResponse;

export enum FieldType {
  Signature = 'signature',
  Initials = 'initials',
  Date = 'date',
  Text = 'text',
  Checkbox = 'checkbox',
}

export enum InputType {
  Client = 'client',
  Fixed = 'fixed',
  AutoFill = 'autoFill',
  Variable = 'variable',
}
export interface AddContractTemplateInput {
  name: string;
  fileKey: string;
  identityId: string;
  fields: ContractField[];
}

export type ClientId = string;

export interface ContractRecipient {
  clientId: string;
  companyId: string;
}

export type VariableInputLabel = string;
export type VariableInputValue = string;

export interface ShareContractTemplateInput {
  contractTemplateId: string;
  clientId: string;
  companyId: string;
  variableValues?: Record<VariableInputLabel, VariableInputValue>;
  creationMode: 'template';
}

interface CreateContractInput extends AddContractTemplateInput {
  recipientId: ClientId;
  clientId: ClientId;
  companyId: string;
  creationMode: 'oneOff';
}

export enum ContractStatus {
  Pending = 'pending',
  Signed = 'signed',
}

export interface ContractTemplate extends BaseEntity {
  id: string;
  name: string;
  fileKey: string;
  fields: ContractField[];
  defaultListIndexPkey: string;
  requestsCount: number;
  submissionsCount: number;
  pageKeys: string[];
  latestSubmissionDate?: string | null;
  createdAt: string;
}

export interface ContractTemplateCounts {
  templateId: string;
  requestsCount: number;
  submissionsCount: number;
  latestSubmissionDate?: string | null;
}

type InternalGetContractParams = {
  contractTemplateId: string;
};

type ClientGetContractParams = {
  recipientId: string;
};

type GetContractParams =
  | InternalGetContractParams
  | ClientGetContractParams
  | object;

/**
 * Generates an error message when the share contract request fails.
 * Depending on the number of users, the error message is updated.
 * @param users - Array of user profiles
 * @returns Error message indicating failed contract sharing
 */

export function generateErrorMessageForShareContract(users: UserProfile[]) {
  const userNames = users.map((user) => `${user.firstName} ${user.lastName}`);

  if (userNames.length === 1) {
    return `Something went wrong sending the contract to ${userNames.at(0)}.`;
  }

  const limitedUsers = userNames.slice(0, 2).join(' and ');

  if (userNames.length > 2) {
    return `Something went wrong sending the contract to ${limitedUsers} and ${
      userNames.length - 2
    } more.`;
  }

  return `Something went wrong sending the contract to ${limitedUsers}.`;
}

async function loadContracts(
  params: GetContractParams,
  previousItems: Contract[],
  nextToken?: string,
): Promise<Contract[]> {
  let allItems = [...previousItems];
  let currentNextToken = nextToken;

  while (true) {
    const response: ListContractResponse = await API.get(
      AppAPIName,
      `/contracts`,
      {
        queryStringParameters: {
          ...params,
          limit: 500,
          nextToken: currentNextToken,
        },
      },
    );

    const responseItems = response && response.data ? response.data : [];
    allItems = [...allItems, ...responseItems];

    if (!response.nextToken) {
      break;
    }

    currentNextToken = response.nextToken;
  }

  return allItems;
}

export const contractsApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    createContractFiles: build.mutation<CreateContractFilesResponse, string>({
      query: (fileKey: string) => ({
        path: `/contract-files`,
        method: ApiMethods.post,
        options: {
          body: {
            fileKey,
          },
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const error = ensureApiError(err);
          notify({
            status: 'error',
            error,
            dispatch,
          });
        }
      },
    }),
    getContractFileStatus: build.query<ContractFileResponse, string>({
      query: (fileKey: string) => ({
        // fileKey contains slashes (it's the S3 path) so we URL-encode it
        // explicitly. API Gateway REST APIs preserve %2F, so the backend's
        // /contract-files/:fileKey route still sees one segment.
        path: `/contract-files/${encodeURIComponent(fileKey)}`,
        method: ApiMethods.get,
        options: {},
      }),
    }),
    addContractTemplate: build.mutation<
      ContractTemplate,
      AddContractTemplateInput & { successMessage?: string }
    >({
      query: (body) => ({
        path: `/contract-templates`,
        method: ApiMethods.post,
        options: {
          body,
        },
      }),
      invalidatesTags: [ApiTags.contract_templates],
      async onQueryStarted(
        { successMessage = 'Contract template created.' },
        { dispatch, queryFulfilled },
      ) {
        try {
          await queryFulfilled;
          notify({
            status: 'success',
            successMessage,
            dispatch,
          });
        } catch (error) {
          notify({
            status: 'error',
            errorMessage: 'The contract template cannot be created.',
            error,
            dispatch,
          });
        }
      },
    }),
    updateContractTemplate: build.mutation<
      ContractTemplate,
      { id: string; body: ContractTemplate }
    >({
      query: ({ id, body }) => ({
        path: `/contract-templates/${id}`,
        method: ApiMethods.put,
        options: {
          body,
        },
      }),
      invalidatesTags: [ApiTags.contract_templates],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          notify({
            status: 'success',
            successMessage: 'Contract template updated.',
            dispatch,
          });
        } catch (error) {
          notify({
            status: 'error',
            errorMessage: 'The contract template cannot be updated.',
            error,
            dispatch,
          });
        }
      },
    }),
    archiveContractTemplate: build.mutation<
      ContractTemplate,
      { contractTemplateId: string }
    >({
      query: ({ contractTemplateId }) => ({
        path: `/contract-templates/${contractTemplateId}`,
        method: ApiMethods.del,
        options: {},
      }),
      invalidatesTags: [ApiTags.contract_templates],
      async onQueryStarted(
        { contractTemplateId },
        { dispatch, queryFulfilled },
      ) {
        const patch = dispatch(
          contractsApi.util.updateQueryData(
            'getContractTemplates',
            undefined,
            (draft) =>
              draft.filter(
                (contractTemplate: ContractTemplate) =>
                  contractTemplate.id !== contractTemplateId,
              ),
          ),
        );

        try {
          await queryFulfilled;
          notify({
            status: 'success',
            successMessage: 'Contract template archived.',
            dispatch,
          });
        } catch (error) {
          patch.undo();
          notify({
            status: 'error',
            errorMessage: 'The contract template cannot be archived.',
            error,
            dispatch,
          });
        }
      },
    }),
    getContractTemplates: build.query<ContractTemplate[], void>({
      query: () => {
        return {
          path: `/contract-templates`,
          method: ApiMethods.get,
          options: {},
        };
      },
      providesTags: [ApiTags.contract_templates],
    }),
    getContractTemplatesLite: build.query<ContractTemplate[], void>({
      query: () => ({
        path: `/contract-templates/list`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: [ApiTags.contract_templates],
    }),
    getContractTemplateCounts: build.query<ContractTemplateCounts[], void>({
      query: () => ({
        path: `/contract-templates/counts`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: [ApiTags.contract_templates],
    }),
    getContracts: build.query<Contract[], GetContractParams>({
      queryFn: async (params: GetContractParams) => {
        // fetch contracts with pagination
        const response = await loadContracts(params, []);
        return {
          data: response,
        };
      },
      providesTags: [ApiTags.contracts],
    }),
    shareContractTemplate: build.mutation<
      ContractTemplate,
      ShareContractTemplateInput | CreateContractInput
    >({
      query: (body) => ({
        path: `/contracts`,
        method: ApiMethods.post,
        options: {
          body,
        },
      }),
    }),
    signContract: build.mutation<Contract, { id: string; body: Contract }>({
      query: ({ id, body }) => ({
        path: `/contracts/${id}`,
        method: ApiMethods.put,
        options: {
          body,
        },
      }),
      invalidatesTags: [ApiTags.contracts],
      async onQueryStarted(_unusedArg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          notify({
            status: 'success',
            successMessage: 'Contract signed successfully.',
            dispatch,
          });
          history.push(CONTRACTS_PAGE.path);
        } catch (error) {
          notify({
            status: 'error',
            errorMessage: 'Failed to sign the contract',
            error,
            dispatch,
          });
        }
      },
    }),
    cancelContract: build.mutation<Contract, { id: string }>({
      query: ({ id }) => ({
        path: `/contracts/${id}`,
        method: ApiMethods.del,
        options: {},
      }),
      invalidatesTags: [ApiTags.contracts, ApiTags.contract_templates],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          contractsApi.util.updateQueryData('getContracts', {}, (draft) =>
            draft.filter((contract: Contract) => contract.id !== id),
          ),
        );

        try {
          await queryFulfilled;
          notify({
            status: 'success',
            successMessage: 'Contract request cancelled.',
            dispatch,
          });
        } catch (error) {
          patch.undo();
          notify({
            status: 'error',
            errorMessage: 'The contract request cannot be cancelled.',
            error,
            dispatch,
          });
        }
      },
    }),
  }),
});

export const {
  useCreateContractFilesMutation,
  useGetContractFileStatusQuery,
  useLazyGetContractFileStatusQuery,
  useAddContractTemplateMutation,
  useUpdateContractTemplateMutation,
  useArchiveContractTemplateMutation,
  useGetContractTemplatesQuery,
  useGetContractTemplatesLiteQuery,
  useGetContractTemplateCountsQuery,
  useGetContractsQuery,
  useShareContractTemplateMutation,
  useSignContractMutation,
  useCancelContractMutation,
} = contractsApi;
